import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { AlertTriangle, ShieldCheck, Check, X, Brain, Sparkles, MessageSquare, ClipboardCheck } from "lucide-react";
import { Skeleton } from "@/components/Skeleton";

const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem("fl_token") || ""}` });

const RAG = {
  red:   { color: "#EF4444", label: "RED — Immediate review" },
  amber: { color: "#F59E0B", label: "AMBER — Monitor" },
  green: { color: "#22C55E", label: "GREEN — Informational" },
};

function riskColor(risk) {
  return risk === "high" ? "#EF4444" : risk === "moderate" ? "#F59E0B" : "#22C55E";
}

function RagChip({ priority }) {
  const r = RAG[priority] || RAG.amber;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
          style={{ background: `color-mix(in oklab, ${r.color} 18%, transparent)`, color: r.color, border: `1px solid color-mix(in oklab, ${r.color} 40%, transparent)` }}
          data-testid={`rag-${priority}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.color }} />
      {priority?.toUpperCase() || "AMBER"}
    </span>
  );
}

function AlertRow({ a, onAck }) {
  return (
    <div className="py-4 border-b last:border-b-0" style={{ borderColor: "var(--fl-border)" }} data-testid={`alert-${a.id}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-1">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-sm font-semibold truncate" style={{ color: "var(--fl-text)" }}>{a.patient_name}</span>
          <RagChip priority={a.priority} />
        </div>
        <button onClick={() => onAck(a.id)} className="text-xs fl-btn-ghost px-2 py-1 rounded-md flex-shrink-0" data-testid={`ack-${a.id}`}>Acknowledge</button>
      </div>
      <div className="text-xs" style={{ color: "var(--fl-muted)" }}>{a.ai_summary || a.message}</div>
      {a.recommended_actions?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {a.recommended_actions.map(([sev, txt], i) => {
            const sc = RAG[sev]?.color || "var(--fl-muted-2)";
            return (
              <li key={i} className="text-[11px] flex items-start gap-1.5">
                <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sc }} />
                <span style={{ color: "var(--fl-muted)" }}>{txt}</span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="text-[10px] mt-2" style={{ color: "var(--fl-muted-2)" }}>
        {new Date(a.created_at).toLocaleString()} · {a.kind}
      </div>
    </div>
  );
}

function ProposalRow({ p, onDecide }) {
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(p.suggested_response || "");
  const decide = async (action, payload = {}) => {
    try {
      await axios.post(`${API}/ai/proposals/${p.id}/${action}`, payload, { headers: HEADERS() });
      toast.success(action === "approve"
        ? (payload.edited_response ? "Edited advice sent to patient." : "Advice sent to patient.")
        : "Proposal rejected.");
      onDecide(p.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed."); }
  };
  return (
    <div className="py-4 border-b last:border-b-0" style={{ borderColor: "var(--fl-border)" }} data-testid={`proposal-${p.id}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold" style={{ color: "var(--fl-text)" }}>{p.patient_name}</span>
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: "color-mix(in oklab, var(--fl-orange) 18%, transparent)", color: "var(--fl-orange)" }}>
            {p.kind}
          </span>
        </div>
        <span className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>{new Date(p.created_at).toLocaleString()}</span>
      </div>
      <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--fl-muted-2)" }}>{p.exercise_hint}</div>
      {p.conversation_excerpt && (
        <div className="mb-2 p-2 rounded-md text-[11px]"
             style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)", color: "var(--fl-muted)" }}>
          <div className="fl-label text-[9px]">Patient said</div>
          <div className="mt-0.5">"{p.conversation_excerpt}"</div>
        </div>
      )}
      <div className="text-xs grid grid-cols-2 gap-2 mb-2">
        <div className="p-2 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }}>
          <div className="fl-label text-[9px]">Current</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--fl-text)" }}>{p.current || "—"}</div>
        </div>
        <div className="p-2 rounded-md" style={{ background: "color-mix(in oklab, var(--fl-orange) 8%, var(--fl-bg-2))", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, var(--fl-border))" }}>
          <div className="fl-label text-[9px]" style={{ color: "var(--fl-orange)" }}>Proposed</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--fl-text)" }}>{p.proposed || "—"}</div>
        </div>
      </div>
      <div className="text-[11px] italic mb-3" style={{ color: "var(--fl-muted)" }}>
        <MessageSquare size={11} className="inline -mt-0.5 mr-1" />
        {p.reasoning}
      </div>
      {p.suggested_response && !editing && (
        <div className="mb-3 p-2 rounded-md text-xs"
             style={{ background: "color-mix(in oklab, var(--fl-orange) 8%, var(--fl-bg-2))", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, var(--fl-border))" }}
             data-testid={`prop-suggested-${p.id}`}>
          <div className="fl-label text-[9px]" style={{ color: "var(--fl-orange)" }}>Suggested reply for patient</div>
          <div className="mt-1" style={{ color: "var(--fl-text)" }}>{p.suggested_response}</div>
        </div>
      )}
      {editing && (
        <textarea
          value={edited}
          onChange={(e) => setEdited(e.target.value)}
          rows={4}
          className="fl-input w-full text-sm px-3 py-2 rounded-md mb-2"
          placeholder="Edit the reply the patient will see…"
          data-testid={`prop-edit-textarea-${p.id}`}
        />
      )}
      <div className="flex gap-2 flex-wrap">
        {!editing ? (
          <>
            <button onClick={() => decide("approve")}
                    className="fl-btn text-xs inline-flex items-center gap-1"
                    data-testid={`prop-accept-${p.id}`}>
              <Check size={13} /> Accept Advice
            </button>
            <button onClick={() => setEditing(true)}
                    className="fl-btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`prop-change-${p.id}`}>
              <MessageSquare size={13} /> Change Advice
            </button>
            <button onClick={() => {
              const note = prompt("Reject reason (optional):", "") || "";
              decide("reject", { note });
            }}
                    className="fl-btn-ghost text-xs inline-flex items-center gap-1 hover:text-red-400"
                    data-testid={`prop-reject-${p.id}`}>
              <X size={13} /> Reject
            </button>
          </>
        ) : (
          <>
            <button onClick={() => decide("approve", { edited_response: edited })}
                    className="fl-btn text-xs inline-flex items-center gap-1"
                    data-testid={`prop-send-edited-${p.id}`}>
              <Check size={13} /> Send edited reply
            </button>
            <button onClick={() => { setEditing(false); setEdited(p.suggested_response || ""); }}
                    className="fl-btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`prop-cancel-edit-${p.id}`}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ClinicianMonitoring() {
  const [caseload, setCaseload] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, p] = await Promise.all([
        axios.get(`${API}/coach/monitor`, { headers: HEADERS() }),
        axios.get(`${API}/coach/alerts`, { headers: HEADERS() }),
        axios.get(`${API}/ai/proposals?status=pending`, { headers: HEADERS() }),
      ]);
      setCaseload(c.data); setAlerts(a.data); setProposals(p.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load monitoring data.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const ack = async (id) => {
    try {
      await axios.post(`${API}/coach/alerts/${id}/ack`, {}, { headers: HEADERS() });
      setAlerts((s) => s.filter((x) => x.id !== id));
    } catch { toast.error("Couldn't acknowledge."); }
  };

  const removeProposal = (id) => setProposals((s) => s.filter((p) => p.id !== id));

  const risks = caseload.reduce((acc, p) => { if (p.risk) acc[p.risk] = (acc[p.risk] || 0) + 1; return acc; }, {});
  const avg = caseload.filter((p) => p.score != null).reduce((s, p) => s + p.score, 0) / (caseload.filter((p) => p.score != null).length || 1);

  return (
    <DashLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="fl-label text-[color:var(--fl-orange)] mb-1">AI Monitoring</div>
          <h1 className="fl-heading text-3xl md:text-4xl">Caseload at a glance.</h1>
          <p className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>
            Recovery scores, RAG-priority alerts and AI Coach adjustment proposals — all under your control.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="fl-card fl-card-elevated p-4"><div className="fl-label">Active patients</div><div className="fl-heading text-3xl mt-1">{loading ? <Skeleton className="h-8 w-14 mt-1 rounded-md" /> : caseload.length}</div></div>
          <div className="fl-card fl-card-elevated p-4"><div className="fl-label">Avg score</div><div className="fl-heading text-3xl mt-1">{loading ? <Skeleton className="h-8 w-14 mt-1 rounded-md" /> : (Math.round(avg) || "—")}</div></div>
          <div className="fl-card fl-card-elevated p-4"><div className="fl-label">Open alerts</div><div className="fl-heading text-3xl mt-1 text-[color:var(--fl-orange)]">{loading ? <Skeleton className="h-8 w-14 mt-1 rounded-md" /> : alerts.length}</div></div>
          <div className="fl-card fl-card-elevated p-4"><div className="fl-label">Pending AI proposals</div><div className="fl-heading text-3xl mt-1 text-[color:var(--fl-orange)]">{loading ? <Skeleton className="h-8 w-14 mt-1 rounded-md" /> : proposals.length}</div></div>
          <div className="fl-card fl-card-elevated p-4">
            <div className="fl-label">Risk mix</div>
            <div className="flex gap-2 mt-2 text-xs">
              <span style={{ color: "#22C55E" }}>● {risks.low || 0} low</span>
              <span style={{ color: "#F59E0B" }}>● {risks.moderate || 0} mod</span>
              <span style={{ color: "#EF4444" }}>● {risks.high || 0} high</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Caseload table */}
          <div className="lg:col-span-2 fl-card fl-card-elevated p-0 overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--fl-border)" }}>
              <div>
                <div className="fl-label">Patients</div>
                <div className="fl-heading text-lg">Monitoring board</div>
              </div>
              <button onClick={load} className="text-xs fl-btn-ghost px-2 py-1 rounded-md" data-testid="refresh-monitor">Refresh</button>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--fl-border)" }} data-testid="caseload-list">
              {loading && (
                <div className="divide-y" data-testid="monitor-loading" style={{ borderColor: "var(--fl-border)" }}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="p-4 flex items-center gap-3">
                      <Skeleton className="w-2 h-8 rounded-full" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-40 rounded-md" />
                        <Skeleton className="h-3 w-56 mt-2 rounded" />
                      </div>
                      <Skeleton className="h-8 w-16 rounded-md" />
                    </div>
                  ))}
                </div>
              )}
              {!loading && caseload.length === 0 && <div className="p-6 text-sm" style={{ color: "var(--fl-muted-2)" }}>No patients assigned to you yet.</div>}
              {caseload.map((p) => (
                <div key={p.patient_id} className="p-4 flex items-center gap-4" data-testid={`row-${p.patient_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold" style={{ color: "var(--fl-text)" }}>{p.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>Last check-in: {p.last_checkin_date || "—"}</div>
                  </div>
                  <div className="text-right min-w-[60px]">
                    <div className="fl-heading text-2xl fl-num-glow" style={{ color: p.score == null ? "var(--fl-muted-2)" : riskColor(p.risk) }}>
                      {p.score ?? "—"}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: p.score == null ? "var(--fl-muted-2)" : riskColor(p.risk) }}>{p.risk ?? "no data"}</div>
                  </div>
                  <div className="text-right min-w-[70px]">
                    <div className="text-sm">{p.compliance_pct ?? "—"}{p.compliance_pct != null ? "%" : ""}</div>
                    <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>compliance</div>
                  </div>
                  <div className="text-right min-w-[70px]">
                    <div className="text-sm" style={{ color: p.open_alerts > 0 ? "var(--fl-orange)" : "var(--fl-muted-2)" }}>{p.open_alerts}</div>
                    <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>alerts</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: proposals + alerts */}
          <div className="space-y-4">
            {/* AI Proposals */}
            <div className="fl-card fl-card-elevated p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="fl-label inline-flex items-center gap-1"><Brain size={11} /> AI approval queue</div>
                  <div className="fl-heading text-lg">Proposed adjustments</div>
                </div>
                {proposals.length > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "var(--fl-orange)", color: "#000" }} data-testid="proposals-count">{proposals.length}</span>
                )}
              </div>
              <div data-testid="proposals-list">
                {proposals.length === 0 && (
                  <div className="text-sm py-4 text-center flex flex-col items-center gap-2" style={{ color: "var(--fl-muted-2)" }}>
                    <Sparkles size={16} className="text-[color:var(--fl-orange)]" />
                    No AI proposals awaiting review.
                  </div>
                )}
                {proposals.map((p) => <ProposalRow key={p.id} p={p} onDecide={removeProposal} />)}
              </div>
              <div className="mt-2 text-[10px] flex items-start gap-1 pt-2 border-t" style={{ color: "var(--fl-muted-2)", borderColor: "var(--fl-border)" }}>
                <ClipboardCheck size={11} className="mt-0.5 flex-shrink-0" />
                <span>AI suggestions never reach a patient without your explicit approval. Every action is written to the AI audit log.</span>
              </div>
            </div>

            {/* Alerts */}
            <div className="fl-card fl-card-elevated p-4">
              <div className="fl-label mb-1">Alerts</div>
              <div className="fl-heading text-lg mb-2">Needs attention</div>
              <div data-testid="alerts-list">
                {alerts.length === 0 && (
                  <div className="text-sm py-6 text-center flex flex-col items-center gap-2" style={{ color: "var(--fl-muted-2)" }}>
                    <ShieldCheck size={22} className="text-[#22C55E]" />
                    All clear — no open alerts.
                  </div>
                )}
                {alerts.map((a) => <AlertRow key={a.id} a={a} onAck={ack} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashLayout>
  );
}
